import { isHostEmbed } from '@/utils/environment/surface';

/**
 * The host's home. Embedded, the host page owns the URL, so its root path is
 * where "back" goes when there is no history behind us.
 */
const HOST_HOME_PATH = '/';

/**
 * Whether the host has navigated since this app booted.
 *
 * `window.history.length` cannot answer this: a tab that opened the preset URL
 * directly still reports a length above one (the blank page it replaced), and a
 * reload keeps the whole stack. Both would send `history.back()` somewhere the
 * user never was — out of the site, or nowhere at all.
 *
 * A host navigation, on the other hand, is something this app observes: the
 * route it reads changes (see `HostRoutePreset`). Sticky once set — after one
 * host navigation there is always a host page behind us.
 */
let hostHasNavigated = false;

/** Called by `HostRoutePreset` when the host route changes under this app. */
export const markHostNavigation = (): void => {
  hostHasNavigated = true;
};

/**
 * Hands "back" to the host page.
 *
 * Embedded, this app's screens are not places the user navigated to — the host
 * page is. They arrived at the preset from gen.ai's home or a category page, so
 * backing out of it belongs to gen.ai's history, not to this app's idea of what
 * sits underneath (its own library, which the user never asked for).
 *
 * Reached the preset URL directly — typed, shared, or reloaded — there is no
 * such page to return to, and `history.back()` would either do nothing or leave
 * the site. Then back means the host's home: a full navigation, since this app
 * cannot drive the host's router.
 *
 * Returns `false` only when this app is not embedded, so the surfaces that own
 * their own URL keep their own back behaviour.
 */
const goBackToHost = (): boolean => {
  if (!isHostEmbed()) return false;

  if (hostHasNavigated) {
    window.history.back();
    return true;
  }

  window.location.assign(HOST_HOME_PATH);
  return true;
};

export default goBackToHost;
